import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useT } from "@/lib/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";
import LanguageToggle from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Anchor, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";

const HERO_BG = "https://images.pexels.com/photos/30710213/pexels-photo-30710213.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function ResetPassword() {
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const missingToken = !token;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr(t("reset.tooShort")); return; }
    if (password !== confirm) { setErr(t("reset.mismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: password });
      setDone(true);
    } catch (ex) {
      const msg = formatApiErrorDetail(ex.response?.data?.detail) || ex.message;
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-[#050A14]">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO_BG} alt="Yacht" className="absolute inset-0 w-full h-full object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#050A14]/60 via-[#050A14]/70 to-[#050A14]" />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 xl:p-14">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md bg-[#007AFF]/15 border border-[#007AFF]/40 flex items-center justify-center">
              <Anchor className="w-6 h-6 text-[#4DA6FF]" />
            </div>
            <div>
              <div className="font-display text-lg font-black tracking-tight">LASOVITA LEDGER</div>
              <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("login.operationalSystem")}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10 relative">
        <div className="absolute top-4 right-4 z-10"><LanguageToggle compact /></div>
        <div className="w-full max-w-md" data-testid="reset-form">
          {done ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <div className="w-14 h-14 rounded-full bg-[#10B981]/15 border border-[#10B981]/40 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7 text-[#34D399]" />
              </div>
              <h2 className="font-display text-2xl font-bold tracking-tight">{t("reset.doneTitle")}</h2>
              <p className="text-sm text-[#94A3B8]">{t("reset.doneBody")}</p>
              <Button onClick={() => navigate("/login")} data-testid="reset-back-to-signin"
                className="bg-[#007AFF] hover:bg-[#0056B3] text-white font-medium tracking-wide">
                {t("reset.signInNow")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          ) : missingToken ? (
            <div className="text-center space-y-4" data-testid="reset-missing-token">
              <div className="w-14 h-14 rounded-full bg-[#EF4444]/15 border border-[#EF4444]/40 flex items-center justify-center mx-auto">
                <AlertCircle className="w-7 h-7 text-[#F87171]" />
              </div>
              <h2 className="font-display text-2xl font-bold tracking-tight">{t("reset.missingTokenTitle")}</h2>
              <p className="text-sm text-[#94A3B8]">{t("reset.missingTokenBody")}</p>
              <Link to="/login" className="inline-block text-[#4DA6FF] text-sm underline-offset-4 hover:underline" data-testid="reset-back-link">
                {t("login.backToSignIn")}
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <div className="font-mono text-[11px] tracking-widest text-[#4DA6FF] mb-3">{t("reset.label")}</div>
                <h2 className="font-display text-3xl font-bold tracking-tight mb-2">{t("reset.title")}</h2>
                <p className="text-sm text-[#94A3B8]">{t("reset.subtitle")}</p>
              </div>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="new-password" className="text-xs font-mono tracking-widest text-[#94A3B8]">{t("reset.newPassword")}</Label>
                  <Input
                    id="new-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password" minLength={8} required
                    data-testid="reset-new-password-input"
                    className="mt-1.5 bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] focus-visible:ring-[#007AFF] focus-visible:border-[#007AFF]"
                  />
                  <div className="text-[10px] text-[#94A3B8] mt-1 font-mono">{t("login.passwordMin")}</div>
                </div>
                <div>
                  <Label htmlFor="confirm-password" className="text-xs font-mono tracking-widest text-[#94A3B8]">{t("reset.confirmPassword")}</Label>
                  <Input
                    id="confirm-password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password" minLength={8} required
                    data-testid="reset-confirm-password-input"
                    className="mt-1.5 bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] focus-visible:ring-[#007AFF] focus-visible:border-[#007AFF]"
                  />
                </div>
                {err && (
                  <div className="text-xs text-[#F87171] font-mono bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-md p-3" data-testid="reset-error">
                    {err}
                  </div>
                )}
                <Button type="submit" disabled={loading} data-testid="reset-submit-btn"
                  className="w-full bg-[#007AFF] hover:bg-[#0056B3] text-white font-medium tracking-wide">
                  {loading ? t("reset.submitting") : t("reset.submit")}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <div className="text-center">
                  <Link to="/login" className="text-xs text-[#94A3B8] hover:text-[#4DA6FF]" data-testid="reset-back-link">
                    {t("login.backToSignIn")}
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
